import { ensureJson, IAnyResult, RestClient } from 'slipstream-components';
import { AppUtil, IEntityBase } from 'ui-common';

interface IRoleEntity extends IEntityBase {}

const restClient = new RestClient();
const iamAPI = {
  async authenticate(authData: any, handle401: boolean = true): Promise<IAnyResult> {
    return restClient.rpc('/v3/auth/tokens?nocatalog', authData, null, handle401);
  },

  async getProjects(extraHeaders?: any): Promise<IAnyResult> {
    return restClient.get('/v3/auth/projects', null, extraHeaders);
  },

  async getUserCapabilities(): Promise<IAnyResult> {
    return restClient.rpc('/iamsvc/get-user-capabilities', {});
  },

  async getAvailableRoles(roleScope: string): Promise<IRoleEntity[]> {
    const resp = restClient.post('/iamsvc/get-user-available-roles', {
      input: { filter_roles: false },
    });
    const body = ensureJson((await resp).body);
    const rs = body.output.roles.find(r => r.scope === roleScope);
    return rs.scope_roles;
  },
  async renewToken(password: string): Promise<any> {
    return AppUtil.context.renewToken(password);
  },

  async getIdpForUser(userName?: string) {
    const inputObj = userName
      ? {
          input: {
            username: userName,
          },
        }
      : {
          input: {},
        };

    const resp = await restClient.post('/iamsvc-noauth/get-idp-for-user', inputObj);
    return ensureJson(resp.body);
  },

  async resetPassword(emailAddress: string) {
    const postObj = {
      input: {
        user_name: emailAddress,
      },
    };

    const resp = await restClient.post('/iamsvc-noauth/forgot-password', postObj);
    return ensureJson(resp.body);
  },
  async federationAuth(idpName: string) {
    const tokenUrl = '/v3/OS-FEDERATION/identity_providers/' + idpName + '/protocols/mapped/auth';
    return restClient.get(tokenUrl);
  },
  async verifyActivationCode(activationCode: string) {
    const postObj = {
      input: {
        activation_code: activationCode,
      },
    };

    return restClient.post('/iamsvc-noauth/verify-activation-code/', postObj);
  },
  async sendNewActivationLink(activationCode: string) {
    const postObj = {
      input: {
        activation_code: activationCode,
      },
    };
    return restClient.post('/iamsvc-noauth/renew-activation-link/', postObj);
  },
  async activateUser(activationCode: string, password: string, acceptUsagePolicy: boolean) {
    const postObj = {
      input: {
        activation_code: activationCode,
        new_password: password,
        accept_usage_policy: acceptUsagePolicy,
      },
    };
    return restClient.post('/iamsvc-noauth/activate-user/', postObj);
  },
  async changePassword(userId: string, userName: string, oldPassword: string, password: string) {
    const postObj = {
      input: {
        user_id: userId,
        user_name: userName,
        old_password: oldPassword,
        new_password: password,
      },
    };
    return restClient.post('/iamsvc-noauth/change-password/', postObj);
  },
  async acceptUsagePolicy(userId: string, policyId: string, policyDate: string) {
    const postObj = {
      input: {
        user_id: userId,
        policy_id: policyId,
        accepted_policy_date: policyDate,
      },
    };
    return restClient.post('/iamsvc-noauth/accept-usage-policy/', postObj);
  },

  async getAboutInfo() {
    const resp = await restClient.get('/about/versions');
    return ensureJson(resp.body);
  },
  async sendCodeViaEmail(payload: any) {
    const response = await restClient.post(
      '/iamsvc-noauth/send-verification-code-via-email',
      payload
    );
    const result = ensureJson(response.body).output;
    return result;
  },
  async getCustomers() {
    const response = await restClient.get(
      '/tssm/customer?fields=parent_uuid,user_defined_properties'
    );
    const result = ensureJson(response.body);
    return result.customer;
  },
};

export default iamAPI;
